import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import AppImage from '../../../components/AppImage';

const BookingManagement = ({ bookings, courts, onBookingAction }) => {
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBooking, setSelectedBooking] = useState(null);

  const filteredBookings = bookings?.filter((booking) => {
    const matchesFilter = filter === 'all' || booking?.status === filter;
    const matchesSearch = 
      booking?.customerName?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      booking?.courtName?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { 
        label: 'Confirmado', 
        className: 'bg-success/10 text-success border-success/20' 
      },
      pending_approval: { 
        label: 'Pendente', 
        className: 'bg-warning/10 text-warning border-warning/20' 
      },
      cancelled: { 
        label: 'Cancelado', 
        className: 'bg-destructive/10 text-destructive border-destructive/20' 
      }
    };
    
    const config = statusConfig?.[status] || statusConfig?.pending_approval;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (status) => {
    const statusConfig = {
      paid: { 
        label: 'Pago', 
        className: 'bg-success/10 text-success border-success/20' 
      },
      pending: { 
        label: 'Pendente', 
        className: 'bg-warning/10 text-warning border-warning/20' 
      },
      overdue: { 
        label: 'Em Atraso', 
        className: 'bg-destructive/10 text-destructive border-destructive/20' 
      }
    };
    
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  const handleBookingAction = (bookingId, action) => {
    onBookingAction?.(bookingId, action);
    setSelectedBooking(null);
  };

  const BookingDetailModal = ({ booking, onClose }) => {
    if (!booking) return null;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-card border border-border rounded-lg shadow-elevation-4 max-w-md w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-heading font-bold text-card-foreground">
                Detalhes da Reserva
              </h3>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                iconName="X"
                iconSize={16}
              />
            </div>

            <div className="space-y-4">
              {/* Customer Info */}
              <div className="flex items-center space-x-3 p-3 bg-muted/30 rounded-lg">
                <AppImage
                  src={booking?.customerAvatar}
                  alt={booking?.customerAvatarAlt}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <div className="font-semibold text-card-foreground">
                    {booking?.customerName}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {booking?.contactInfo?.email}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {booking?.contactInfo?.phone}
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Quadra</label>
                  <div className="text-card-foreground">{booking?.courtName}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Data</label>
                  <div className="text-card-foreground">
                    {new Date(booking?.date)?.toLocaleDateString('pt-BR')}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Horário</label>
                  <div className="text-card-foreground">{booking?.time}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Jogadores</label>
                  <div className="text-card-foreground">{booking?.players} pessoas</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Valor</label>
                  <div className="text-card-foreground font-semibold">
                    R$ {booking?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Duração</label>
                  <div className="text-card-foreground">{booking?.duration}h</div>
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center justify-between">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Status</label>
                  <div className="mt-1">{getStatusBadge(booking?.status)}</div>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Pagamento</label>
                  <div className="mt-1">{getPaymentStatusBadge(booking?.paymentStatus)}</div>
                </div>
              </div>

              {/* Actions */}
              {booking?.status === 'pending_approval' && (
                <div className="flex space-x-3 pt-4">
                  <Button
                    variant="default"
                    onClick={() => handleBookingAction(booking?.id, 'approve')}
                    className="flex-1"
                    iconName="Check"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Aprovar
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleBookingAction(booking?.id, 'reject')}
                    className="flex-1"
                    iconName="X"
                    iconPosition="left"
                    iconSize={16}
                  >
                    Recusar
                  </Button>
                </div>
              )}

              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="MessageCircle"
                  iconPosition="left"
                  iconSize={16}
                  className="flex-1"
                >
                  Contatar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="FileText"
                  iconPosition="left"
                  iconSize={16}
                  className="flex-1"
                >
                  Recibo
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground">
            Gerenciamento de Reservas
          </h2>
          <p className="text-muted-foreground">
            Aprove, gerencie e acompanhe todas as reservas
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <Input
            type="text"
            placeholder="Buscar por cliente ou quadra..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
            className="w-64"
            iconName="Search"
            iconSize={16}
          />
          
          <Select
            value={filter}
            onChange={(value) => setFilter(value)}
            className="w-40"
          >
            <option value="all">Todas</option>
            <option value="pending_approval">Pendentes</option>
            <option value="confirmed">Confirmadas</option>
            <option value="cancelled">Canceladas</option>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Icon name="Clock" size={20} className="text-warning" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {bookings?.filter(b => b?.status === 'pending_approval')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Pendentes</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="Check" size={20} className="text-success" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {bookings?.filter(b => b?.status === 'confirmed')?.length || 0}
              </div>
              <div className="text-sm text-muted-foreground">Confirmadas</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="DollarSign" size={20} className="text-primary" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                R$ {bookings?.reduce((sum, b) => sum + (b?.amount || 0), 0)?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </div>
              <div className="text-sm text-muted-foreground">Total</div>
            </div>
          </div>
        </div>

        <div className="bg-card border border-border rounded-lg p-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-info/10 rounded-lg">
              <Icon name="Users" size={20} className="text-info" />
            </div>
            <div>
              <div className="text-lg font-heading font-bold text-card-foreground">
                {bookings?.reduce((sum, b) => sum + (b?.players || 0), 0)}
              </div>
              <div className="text-sm text-muted-foreground">Jogadores</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bookings Table */}
      <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30 border-b border-border">
              <tr>
                <th className="text-left p-4 font-medium text-muted-foreground">Cliente</th>
                <th className="text-left p-4 font-medium text-muted-foreground">Quadra</th>
                <th className="text-left p-4 font-medium text-muted-foreground">Data/Horário</th>
                <th className="text-left p-4 font-medium text-muted-foreground">Valor</th>
                <th className="text-left p-4 font-medium text-muted-foreground">Status</th>
                <th className="text-left p-4 font-medium text-muted-foreground">Pagamento</th>
                <th className="text-left p-4 font-medium text-muted-foreground">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredBookings?.map((booking) => (
                <tr key={booking?.id} className="border-b border-border hover:bg-muted/20">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <AppImage
                        src={booking?.customerAvatar}
                        alt={booking?.customerAvatarAlt}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <div className="font-medium text-card-foreground">
                          {booking?.customerName}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {booking?.players} jogadores
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-card-foreground">
                    {booking?.courtName}
                  </td>
                  <td className="p-4">
                    <div className="text-card-foreground">
                      {new Date(booking?.date)?.toLocaleDateString('pt-BR')}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      {booking?.time}
                    </div>
                  </td>
                  <td className="p-4 font-semibold text-card-foreground">
                    R$ {booking?.amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-4">
                    {getStatusBadge(booking?.status)}
                  </td>
                  <td className="p-4">
                    {getPaymentStatusBadge(booking?.paymentStatus)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedBooking(booking)}
                        iconName="Eye"
                        iconSize={14}
                      />
                      {booking?.status === 'pending_approval' && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleBookingAction(booking?.id, 'approve')}
                            iconName="Check"
                            iconSize={14}
                            className="text-success hover:text-success"
                          />
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleBookingAction(booking?.id, 'reject')}
                            iconName="X"
                            iconSize={14}
                            className="text-destructive hover:text-destructive"
                          />
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredBookings?.length === 0 && (
          <div className="text-center py-12">
            <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-lg mx-auto mb-4">
              <Icon name="Calendar" size={24} className="text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-card-foreground mb-2">
              Nenhuma reserva encontrada
            </h3>
            <p className="text-muted-foreground">
              {filter === 'all' ?'Ainda não há reservas registradas'
                : `Não há reservas com status "${filter}"`}
            </p>
          </div>
        )}
      </div>

      {/* Booking Detail Modal */}
      <BookingDetailModal
        booking={selectedBooking}
        onClose={() => setSelectedBooking(null)}
      />
    </div>
  );
};

export default BookingManagement;